import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Redirect } from 'react-router-dom';
// import classNames from 'classnames';
import ClipLoader from 'react-spinners/ClipLoader';

import MultiSelect from 'react-multi-select-component';

// Local imports
import { useFormatMessage } from 'hooks';
import { fetchEntities, entitiesCleanUp } from 'state/actions/stations';
import {
  fetchEntities as fetchStationsUsersGroupsData,
  entitiesCleanUp as stationsUsersGroupsCleanUp,
} from 'state/actions/stationsUsersGroups';
import { fetchLastEntities as fetchLastStationsData } from 'state/actions/stationsData';
import {
  setMapOptions,
  setMapStyle,
  showFooter,
  hideFooter,
} from 'state/actions/preferences';
import paths from 'pages/Router/paths';
import Map from 'components/Map';
import {
  search as searchInObject,
  stationToMarker,
  valueToSelectOption,
  selectOptionToValue,
} from 'utils';
import CONSTANTS from 'utils/constants';

import './index.scss';

const StationsMap = () => {
  const entitiesName = 'stations';
  const i18nEntityName = 'Stations';
  const i18nScreenName = 'StationsMap';

  const {
    entitiesList = [],
    isAuth,
    isInstaller,
    isAdmin,
    mapOptions,
    mapStyle,
    error,
    loading,
    stationsLastData,
    stationsLastStatus,
    lastDataIsLoading,
    stationsUsersGroups = [],
  } = useSelector(
    (state) => ({
      entitiesList: state[entitiesName].data,
      isAuth: state.auth.userData.role > 0,
      isInstaller: CONSTANTS.IS_USER_ROLE_INSTALLER[state.auth.userData.role],
      isAdmin: CONSTANTS.IS_USER_ROLE_ADMIN[state.auth.userData.role],
      mapOptions: state.preferences.mapOptions,
      mapStyle: state.preferences.mapStyle,
      error: state[entitiesName].error,
      loading: state[entitiesName].loading,
      stationsLastData: state.stationsData.lastData,
      stationsLastStatus: state.stationsData.lastStatus,
      lastDataIsLoading: state.stationsData.lastDataIsLoading,
      stationsUsersGroups: state.stationsUsersGroups.data,
    }),
    shallowEqual
  );

  let defaultSelectedStatusOptions = CONSTANTS.ACTIVE_STATIONS_STATUS.map(
    valueToSelectOption
  );
  let statusOptions = CONSTANTS.STATIONS_STATUS_FOR_STANDARD.map(
    valueToSelectOption
  );
  if (isInstaller) {
    statusOptions = CONSTANTS.STATIONS_STATUS_FOR_INSTALLER.map(
      valueToSelectOption
    );
    defaultSelectedStatusOptions = CONSTANTS.ACTIVE_STATIONS_STATUS_FOR_INSTALLER.map(
      valueToSelectOption
    );
  } else if (isAdmin) {
    statusOptions = CONSTANTS.STATIONS_STATUS.map(valueToSelectOption);
  }

  const [markers, setMarkers] = useState([]);
  const [showLines, setShowLines] = useState(true);
  const [showCurrentPosition, setShowCurrentPosition] = useState(false);
  const [stationsWithGroups, setStationsWithGroups] = useState([]);
  const [selectedStatusOptions, setSelectedStatusOptions] = useState(
    defaultSelectedStatusOptions
  );
  const [search, setSearch] = useState('');
  const [currentPosition, setCurrentPosition] = useState(null);

  const watchPositionId = useRef(null);

  const dispatch = useDispatch();

  // Hide footer to make the map bigger
  useEffect(() => {
    dispatch(hideFooter());

    return () => {
      dispatch(showFooter());
    };
  });

  useEffect(() => {
    if (isAuth && entitiesList.length === 0) {
      dispatch(fetchEntities());
    }

    return () => dispatch(entitiesCleanUp());
  }, [dispatch, isAuth, entitiesList]);

  useEffect(() => {
    if (isAuth && stationsUsersGroups.length === 0) {
      dispatch(fetchStationsUsersGroupsData());
    }

    return () => dispatch(stationsUsersGroupsCleanUp());
  }, [dispatch, isAuth, stationsUsersGroups]);

  useEffect(() => {
    // Add usersStationsGroups in entities
    const newStationsWithGroups = entitiesList.map((station) => {
      const { id } = station;
      const stationsUsersGroupsForStation = stationsUsersGroups.filter(
        (sug) => {
          return sug.stationsIds && sug.stationsIds.includes(id);
        }
      );

      const stationWithGroups = {
        ...station,
        stationsUsersGroups: stationsUsersGroupsForStation,
      };
      return stationWithGroups;
    });
    setStationsWithGroups(newStationsWithGroups);
  }, [entitiesList, stationsUsersGroups]);

  // When stations are ready
  // Fetch stations data
  useEffect(() => {
    if (!lastDataIsLoading) {
      const serialNumbers = entitiesList
        .map((entity) => {
          const { serialNumber } = entity;
          if (typeof serialNumber === 'number') {
            return String(serialNumber);
          }
          return serialNumber;
        })
        .filter((serialNumber) => {
          return serialNumber && serialNumber.length > 0;
        });

      dispatch(fetchLastStationsData(serialNumbers));
    }
  }, [entitiesList]);

  // Effect to filter and generate markers
  useEffect(() => {
    // Filter based on selected status
    const stationsWithSelectedStatus = stationsWithGroups.filter((el) => {
      const selectedStatus = selectedStatusOptions.map(selectOptionToValue);
      const { status } = el;
      if (selectedStatus.includes(status)) {
        return true;
      }
      return false;
    });
    // Filter entities based on search
    const filteredEntities = search
      ? stationsWithSelectedStatus.filter((el) => {
          const clonedElem = {
            address: {
              city: el.address.city,
              country: el.address.country,
            },
            name: el.name,
            serialNumber: el.serialNumber,
          };
          // Delete these fields from search
          delete clonedElem.id;
          // In group, keep only name field
          clonedElem.stationsUsersGroups = el.stationsUsersGroups.map((sug) => {
            return { name: sug.name };
          });

          return searchInObject(clonedElem, search);
        })
      : stationsWithSelectedStatus;

    // Build markers from fileterd entities
    const markersFromEntities = filteredEntities.map((entity) => {
      const stationLastStatus = stationsLastStatus[entity.id];
      return stationToMarker(entity, stationLastStatus);
    });

    setMarkers(markersFromEntities);
  }, [search, selectedStatusOptions, stationsWithGroups, stationsLastData]);

  const startGPS = () => {
    // Get Position if available
    if ('geolocation' in navigator) {
      // Watch position
      if (
        !watchPositionId ||
        watchPositionId.current === null ||
        watchPositionId.current === undefined
      ) {
        // Init
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setCurrentPosition(position);
          },
          (err) => {
            console.error(`Error Code = ${err.code} - ${err.message}`); // eslint-disable-line no-console
          }
        );

        const newWatchPositionId = navigator.geolocation.watchPosition(
          (position) => {
            setCurrentPosition(position);
          },
          (err) => {
            // eslint-disable-next-line no-console
            console.error(`Watch error Code = ${err.code} - ${err.message}`);
          }
        );
        // Save watch id to clear interval
        watchPositionId.current = newWatchPositionId;
      }
    }
  };

  const clearWatchPosition = () => {
    if (navigator.geolocation && watchPositionId) {
      navigator.geolocation.clearWatch(watchPositionId.current);
      watchPositionId.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearWatchPosition();
    };
  }, [watchPositionId]);

  const handleSwitchChanged = (e) => {
    const isChecked = e.currentTarget.checked;
    setShowLines(isChecked);
  };

  const handleMapMoved = ({ center, zoom }) => {
    dispatch(setMapOptions({ center, zoom }));
  };

  const handleMapStyleSwitchChanged = (e) => {
    const isChecked = e.currentTarget.checked;
    const styles = CONSTANTS.MAPBOX.STYLES;
    const style = isChecked ? styles.SATELLITE : styles.STREETS;
    dispatch(setMapStyle({ style }));
  };

  const handleCurrentPositionSwitchChanged = (e) => {
    const isChecked = e.currentTarget.checked;
    if (isChecked) {
      startGPS();
    } else {
      setCurrentPosition(null);
      clearWatchPosition();
    }
    setShowCurrentPosition(isChecked);
  };

  const defaultCenter = mapOptions && mapOptions.center;
  const defaultZoom = mapOptions && mapOptions.zoom;

  const redirect = !isAuth && <Redirect to={paths.ROOT} />;
  return (
    <>
      {redirect}

      <section className="section section-stations-map is-main-section is-flex-grow-1 is-flex is-flex-direction-column">
        <div className="card section-stations-map-filters">
          <header className="card-header is-flex-direction-column">
            <p className="card-header-title">
              <span>{useFormatMessage(`${i18nEntityName}.search`)}</span>
              <input
                type="text"
                className="input ml-2"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </p>
            <div className="card-header-title is-flex-wrap-wrap">
              <input
                id="switchLinesOfSight"
                type="checkbox"
                name="switchLinesOfSight"
                className="switch is-rtl"
                defaultChecked={showLines}
                onChange={handleSwitchChanged}
              />
              <label htmlFor="switchLinesOfSight" className="mt-2">
                {useFormatMessage(`${i18nScreenName}.linesOfSight`)}:
              </label>
              <input
                id="switchMapStyle"
                type="checkbox"
                name="switchMapStyle"
                className="switch is-rtl"
                defaultChecked={mapStyle === CONSTANTS.MAPBOX.STYLES.SATELLITE}
                onChange={handleMapStyleSwitchChanged}
              />
              <label htmlFor="switchMapStyle" className="mt-2 ml-4">
                {useFormatMessage(`${i18nScreenName}.satellite`)}:
              </label>
              <input
                id="switchShowCurrentPosition"
                type="checkbox"
                name="switchShowCurrentPosition"
                className="switch is-rtl"
                defaultChecked={showCurrentPosition}
                onChange={handleCurrentPositionSwitchChanged}
              />
              <label htmlFor="switchShowCurrentPosition" className="mt-2 ml-4">
                {useFormatMessage(`${i18nScreenName}.currentPosition`)}:
              </label>
              <div className="select is-multiple mt-2 ml-4 is-flex is-align-items-center">
                <label htmlFor="switchMapStyle">
                  {useFormatMessage(`${i18nScreenName}.status`)}:
                </label>
                <MultiSelect
                  options={statusOptions}
                  value={selectedStatusOptions}
                  onChange={setSelectedStatusOptions}
                  labelledBy="Select"
                  className="ml-2"
                />
              </div>
              {/* <div>
                {currentPosition &&
                  currentPosition.coords &&
                  currentPosition.coords.accuracy}

                {currentPosition &&
                  currentPosition.coords &&
                  currentPosition.coords.latitude}

                {currentPosition &&
                  currentPosition.coords &&
                  currentPosition.coords.longitude}
              </div> */}
            </div>
          </header>
        </div>
        <div className="stations-map is-flex-grow-1 is-flex is-flex-direction-column">
          <div className="map-loader">
            {lastDataIsLoading ? <ClipLoader /> : ''}
          </div>

          {loading ? (
            <ClipLoader />
          ) : (
            <Map
              center={defaultCenter}
              options={{ zoom: defaultZoom }}
              currentPosition={currentPosition}
              markers={markers}
              showLines={showLines}
              style={mapStyle}
              onMapMoved={handleMapMoved}
            />
          )}
          {error && 'Show error'}
        </div>
      </section>
    </>
  );
};

export default StationsMap;
